/* eslint-disable react/react-in-jsx-scope */
import { CustomButton, CustomDrawer } from '@components';
import { Box, Chip, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import { COLORS } from '@styles';
import { appContext } from '@utils';
import { useFormik } from 'formik';
import { observer } from 'mobx-react';
import React, { useContext, useEffect } from 'react';
import * as Yup from 'yup';
import PercentIcon from '@mui/icons-material/Percent';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { toast } from 'react-toastify';
import NoResultsImage from '@assets/img/no-results-logs.png';
import { RequestStatus } from '@store';

const makeStyles = () => ({
  formWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '85vh',
  } as React.CSSProperties,
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    padding: '0 1rem',
  } as React.CSSProperties,
  formSection: {
    flexShrink: 0,
  } as React.CSSProperties,
  availableSection: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    marginTop: '1.5rem',
  } as React.CSSProperties,
  formItems: {
    margin: '1rem 0 0 0',
    borderRadius: '1rem',
  },
  inputBorder: {
    borderRadius: '1rem',
  },
  description: {
    color: COLORS.NEUTRAL_LIGHT_0,
    fontSize: '0.75rem',
    padding: '0 1rem',
    marginTop: '0.5rem',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    flexShrink: 0,
  },
  discountCardWrapper: {
    border: `1px solid ${COLORS.NEUTRAL_DARK_1}`,
    borderRadius: '0.5rem',
    padding: '1rem',
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: '100%',
  } as React.CSSProperties,
  discountCodeCard: {
    marginBottom: '0.75rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '0.5rem',
  } as React.CSSProperties,
  discountCodeInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    paddingBlock: '0.37rem',
  } as React.CSSProperties,
  discountCodeTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: COLORS.NEUTRAL_WHITE,
  },
  discountCodeValue: {
    fontSize: '0.75rem',
    fontWeight: 400,
    color: COLORS.NEUTRAL_LIGHT_0,
  },
  discountCodeActions: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    textAlign: 'center',
  } as React.CSSProperties,
  emptyStateImage: {
    marginBottom: '1rem',
  },
  emptyStateText: {
    color: COLORS.NEUTRAL_LIGHT_0,
    fontSize: '0.875rem',
  },
  button: {
    padding: '1rem 0 0 0',
  },
  chip: {
    backgroundColor: COLORS.ELEKTRAFI_NEUTRAL,
    color: COLORS.NEUTRAL_WHITE,
    fontWeight: 600,
    fontSize: '0.75rem',
  },
});

interface IManageAllDiscountsDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const ManageAllDiscountsDrawer = observer(
  ({ open, onClose }: IManageAllDiscountsDrawerProps) => {
    const sx = makeStyles();

    const {
      billingStore: {
        discountCodes,
        listDiscountCodes,
        createDiscountCode,
        deleteDiscountCode,
        discountCodesData,
        createDiscountCodeData,
      },
    } = useContext(appContext);

    // Load discount codes when drawer opens
    useEffect(() => {
      if (open) {
        listDiscountCodes();
      }
    }, [open, listDiscountCodes]);

    const validationSchema = Yup.object().shape({
      percentage: Yup.number()
        .required('Discount percentage is required.')
        .min(0, 'Percentage must be at least 0.')
        .max(100, 'Percentage must be at most 100.'),
    });

    const formik = useFormik({
      initialValues: {
        percentage: '',
      },
      validationSchema,
      onSubmit: async (values, { resetForm }) => {
        const result = await createDiscountCode({
          discountPercentage: Number(values.percentage),
        });

        if (result) {
          resetForm();
        }
      },
      validateOnChange: false,
    });

    const handleCopyCode = (code: string) => {
      navigator.clipboard.writeText(code);
      toast('Discount code copied to clipboard!', { type: 'success' });
    };

    const handleDeleteCode = async (id: string) => {
      await deleteDiscountCode(id);
    };

    return (
      <CustomDrawer
        open={open}
        onClose={onClose}
        headerLabel="Discount Codes"
        headerIcon={<PercentIcon />}
      >
        <form style={sx.formWrapper} onSubmit={formik.handleSubmit}>
          <div style={sx.formContainer}>
            <div style={sx.formSection}>
              <Typography variant="subtitle2" style={{ marginTop: '0.5rem' }}>
                Discount Percentage
              </Typography>
              <TextField
                style={sx.formItems}
                fullWidth
                placeholder="Discount Code"
                name="percentage"
                type="number"
                onChange={formik.handleChange}
                value={formik.values.percentage}
                variant="outlined"
                InputProps={{
                  style: sx.inputBorder,
                  startAdornment: (
                    <PercentIcon style={{ marginRight: '0.5rem', color: COLORS.NEUTRAL_LIGHT_0 }} />
                  ),
                }}
                error={!!formik.errors.percentage}
                helperText={formik.errors.percentage}
              />
              <Typography style={sx.description}>
                This discount applies to the organization&apos;s plan and add-ons at checkout. The
                code can be used once and will appear on the organization&apos;s billing page after
                use.
              </Typography>

              <div style={sx.button}>
                <CustomButton
                  variant="transparent"
                  type="submit"
                  isLoading={createDiscountCodeData.status === RequestStatus.LOADING}
                >
                  Create Discount Code
                </CustomButton>
              </div>
            </div>

            <div style={sx.availableSection}>
              <div style={sx.sectionHeader}>
                <Typography variant="subtitle1">Available</Typography>
                <Chip
                  label={`${discountCodes.length} Code${discountCodes.length !== 1 ? 's' : ''}`}
                  style={sx.chip}
                  size="small"
                />
              </div>

              <Box style={sx.discountCardWrapper}>
              {discountCodesData.status === RequestStatus.LOADING && (
                <div style={sx.emptyState}>
                  <CircularProgress size={40} sx={{ color: COLORS.ELEKTRAFI_NEUTRAL }} />
                </div>
              )}
              {discountCodesData.status !== RequestStatus.LOADING && discountCodes.length === 0 && (
                <div style={sx.emptyState}>
                  <img src={NoResultsImage} alt="No discount codes" style={sx.emptyStateImage} />
                </div>
              )}
              {discountCodesData.status !== RequestStatus.LOADING && discountCodes.length > 0 && (
                <>
                {discountCodes.map((discountCode, index) => (
                  <div
                    key={discountCode.id}
                    style={{
                      ...sx.discountCodeCard,
                      // Remove border bottom from last item
                      borderBottom:
                        index === discountCodes.length - 1
                          ? 'none'
                          : `1px solid ${COLORS.NEUTRAL_DARK_1}`,
                      // Remove padding bottom from last item
                      paddingBottom: index === discountCodes.length - 1 ? '0' : '0.5rem',
                      // Remove margin bottom from last item
                      marginBottom: index === discountCodes.length - 1 ? '0' : '0.5rem',
                    }}
                  >
                    <div style={sx.discountCodeInfo}>
                      <Typography style={sx.discountCodeTitle}>
                        %{discountCode.discountPercentage} Off in Plan and Add-Ons
                      </Typography>
                      <Typography style={sx.discountCodeValue}>{discountCode.code}</Typography>
                    </div>
                    <div style={sx.discountCodeActions}>
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(discountCode.code)}
                        sx={{ color: COLORS.NEUTRAL_WHITE }}
                      >
                        <ContentCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteCode(discountCode.id)}
                        sx={{ color: COLORS.NEGATIVE_NEUTRAL }}
                      >
                        <RemoveCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                ))}
                </>
              )}
              </Box>
            </div>
          </div>
        </form>
      </CustomDrawer>
    );
  },
);
